/**
 * Lot 38 (#353, `docs/10-application-tournable.md` § 3) : le mode tournage.
 *
 * `?oo_shoot=1` rend l'application déterministe d'une prise à l'autre :
 * horloge figée (`?oo_now=…`, sinon la date du jeu de démo), aucune
 * animation, polices chargées avant le premier rendu. Il tient pour la session
 * de l'onglet (les routes ne gardent pas les paramètres qu'elles ne lisent
 * pas) ; `?oo_shoot=0` l'arrête.
 *
 * **Jamais en production** : il n'existe qu'en développement ou dans un build
 * de démo (`VITE_OO_DEMO=1`). Dans le build de production, `shootAllowed()`
 * vaut `false` à la compilation et tout le reste est retiré du paquet.
 */

/**
 * La date du jeu de démo d'oo-backend (#354, `DEMO_NOW`) : ses échéances sont
 * relatives à elle ; figer ailleurs les ferait dériver.
 */
export const DEFAULT_SHOOT_NOW = '2026-10-05T14:00:00Z'

const STORAGE_KEY = 'oo:tournage'

export interface ShootState {
  /** L'instant figé, en millisecondes. */
  now: number
}

export function shootAllowed(): boolean {
  return import.meta.env.DEV || import.meta.env.VITE_OO_DEMO === '1'
}

/**
 * Lit l'adresse et la session : le mode est-il demandé, et à quelle date ?
 * Pur (hors `sessionStorage`), pour être testé sans rien figer.
 */
export function readShootRequest(
  search: string,
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>,
): ShootState | null {
  const params = new URLSearchParams(search)
  if (params.get('oo_shoot') === '0') {
    storage.removeItem(STORAGE_KEY)
    return null
  }
  let stored: ShootState | null = null
  try {
    const raw = storage.getItem(STORAGE_KEY)
    stored = raw ? (JSON.parse(raw) as ShootState) : null
  } catch {
    stored = null
  }
  if (params.get('oo_shoot') !== '1' && !stored) return null
  const asked = params.get('oo_now')
  const parsed = asked ? Date.parse(asked) : Number.NaN
  const now = Number.isNaN(parsed) ? (stored?.now ?? Date.parse(DEFAULT_SHOOT_NOW)) : parsed
  const state = { now }
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Stockage refusé : le mode tient pour cette page seulement.
  }
  return state
}

/** Remplace `Date` : sans argument et `Date.now()` rendent l'instant figé. */
export function freezeClock(now: number): void {
  const RealDate = Date
  class FrozenDate extends RealDate {
    constructor(...args: unknown[]) {
      if (args.length === 0) super(now)
      else super(...(args as [string | number | Date]))
    }
    static override now(): number {
      return now
    }
  }
  globalThis.Date = FrozenDate as DateConstructor
}

const NO_MOTION = `
[data-oo-shoot] *, [data-oo-shoot] *::before, [data-oo-shoot] *::after {
  animation-duration: 0.001ms !important;
  animation-delay: 0s !important;
  animation-iteration-count: 1 !important;
  transition-duration: 0.001ms !important;
  transition-delay: 0s !important;
  scroll-behavior: auto !important;
}`

/**
 * À appeler avant le premier rendu. Rend l'état actif, ou `null` : hors
 * développement et démo, rien n'est lu ni figé.
 */
export function startShootMode(): ShootState | null {
  if (!shootAllowed()) return null
  const state = readShootRequest(window.location.search, window.sessionStorage)
  if (!state) return null
  freezeClock(state.now)
  document.documentElement.dataset.ooShoot = ''
  const style = document.createElement('style')
  style.dataset.ooShoot = ''
  style.textContent = NO_MOTION
  document.head.append(style)
  // Le CLI `oo training` vérifie qu'il filme bien en mode tournage.
  Object.defineProperty(window, '__ooShoot', { value: { now: state.now } })
  return state
}
